import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface MentionMember {
  id: string;
  firstName: string;
  lastName: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  members: MentionMember[];
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  onSubmit?: () => void;
  /** Called every keystroke; consumer decides on throttling. */
  onTyping?: () => void;
  /** Called shortly after typing stops (no input for ~4s) or on blur. */
  onStopTyping?: () => void;
  autoFocus?: boolean;
}

const STOP_TYPING_DELAY_MS = 4000;

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  members,
  placeholder,
  rows = 3,
  className,
  disabled,
  onSubmit,
  onTyping,
  onStopTyping,
  autoFocus,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tokenStart, setTokenStart] = useState<number | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const ln = a.lastName.localeCompare(b.lastName, 'pl');
      if (ln !== 0) return ln;
      return a.firstName.localeCompare(b.firstName, 'pl');
    });
  }, [members]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedMembers.slice(0, 8);
    return sortedMembers
      .filter((m) => {
        const full = `${m.firstName} ${m.lastName}`.toLowerCase();
        const rev = `${m.lastName} ${m.firstName}`.toLowerCase();
        return full.includes(q) || rev.includes(q);
      })
      .slice(0, 8);
  }, [sortedMembers, query]);

  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(0);
  }, [filtered, activeIdx]);

  useEffect(() => {
    return () => {
      if (stopTimer.current) clearTimeout(stopTimer.current);
    };
  }, []);

  const triggerStopTyping = () => {
    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = setTimeout(() => {
      onStopTyping?.();
    }, STOP_TYPING_DELAY_MS);
  };

  const detectMentionToken = (text: string, caret: number) => {
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === '@') {
        const before = i > 0 ? text[i - 1] : '';
        if (before === '' || /\s/.test(before)) {
          return { start: i, query: text.slice(i + 1, caret) };
        }
        return null;
      }
      if (/\s/.test(ch) || ch === '\n') return null;
      i--;
    }
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    onChange(next);
    if (next.trim()) {
      onTyping?.();
      triggerStopTyping();
    }
    const caret = e.target.selectionStart ?? next.length;
    const token = detectMentionToken(next, caret);
    if (token) {
      setOpen(true);
      setTokenStart(token.start);
      setQuery(token.query);
    } else {
      setOpen(false);
      setTokenStart(null);
      setQuery('');
    }
  };

  const insertMention = (m: MentionMember) => {
    if (tokenStart == null || !ref.current) return;
    const caret = ref.current.selectionStart ?? value.length;
    const before = value.slice(0, tokenStart);
    const after = value.slice(caret);
    const mention = `@[${m.firstName} ${m.lastName}](${m.id})`;
    const next = `${before}${mention} ${after}`;
    onChange(next);
    setOpen(false);
    setTokenStart(null);
    setQuery('');
    requestAnimationFrame(() => {
      if (ref.current) {
        const pos = before.length + mention.length + 1;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (open && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % filtered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filtered[activeIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
    }
    if (!open && (e.key === 'Enter') && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (stopTimer.current) clearTimeout(stopTimer.current);
          onStopTyping?.();
          setTimeout(() => setOpen(false), 150);
        }}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        className={
          'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y ' +
          (className || '')
        }
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto bg-popover border border-border rounded-md shadow-md py-1">
          {filtered.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(m);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent ${
                idx === activeIdx ? 'bg-accent' : ''
              }`}
            >
              <span className="font-medium">
                {m.lastName} {m.firstName}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
